import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const SelectDropdown = ({
  options = [],
  width = '200px',
  label,
  singleSelectDefaultValue = '',
  multiSelectDefaultValues = [],
  className = '',
  handleFilterChange = () => {},
  resetValues,
  multiple = false,
  disabled = false,
  allOptionsSelectedText = 'All Options',
  updateSingleDropdownValue = '',
  updateMultipleDropdownValue = [],
  isSelectAll = false,
  containerWidth = '',
  handleResetValues = () => {},
  marginExist = true,
  excludeGaleMarketSelWithOtherBranch = false,
  regionalBranches = false,
  regionIdDependencyValue = '',
  value = [],
}) => {
  const [singleSelectValue, setSingleSelectValue] = useState(singleSelectDefaultValue); // single opt which is string i.e. option value
  const [multiSelectValues, setMultiSelectValues] = useState(multiSelectDefaultValues); // array of options, where each opt is string i.e. option value
  const [selectedOptions, setSelectedOptions] = useState(multiSelectDefaultValues);
  let arr1 = options
    ?.filter((opt) => selectedOptions?.includes(opt?.value))
    ?.map((opt) => opt?.label);
  let arr2 = options?.filter((opt) => opt?.label);

  const debouncedHandleFilterChange = useCallback(
    (value) => {
      if (debouncedHandleFilterChange.timer) {
        clearTimeout(debouncedHandleFilterChange.timer);
      }
      debouncedHandleFilterChange.timer = setTimeout(() => {
        handleFilterChange(value);
      }, 1500);
    },
    [handleFilterChange]
  );

  const handleSingleSelectChange = (e) => {
    if (!disabled) {
      setSingleSelectValue(e.target.value);
      handleFilterChange(e.target.value);
    }
  };

  const handleMultiSelectChange = (e) => {
    if (!disabled) {
      setMultiSelectValues(e.target.value);
      if (excludeGaleMarketSelWithOtherBranch) {
        debouncedHandleFilterChange(e.target.value);
      } else {
        handleFilterChange(e.target.value);
      }
    }
  };

  const handleSelectedOption = (value) => {
    if (!disabled) {
      let values;
      if (selectedOptions?.includes(value)) {
        values = selectedOptions.filter((val) => val !== value);
      } else {
        values = [...selectedOptions, value];
      }
      setSelectedOptions(values);
      if (excludeGaleMarketSelWithOtherBranch) {
        debouncedHandleFilterChange(values);
      } else {
        handleFilterChange(values);
      }
    }
  };

  const handleSelectAll = () => {
    if (disabled) return;

    // If there's only one option, toggle it
    if (options.length === 1) {
      const singleOptionValue = options[0].value;
      if (selectedOptions.includes(singleOptionValue)) {
        setSelectedOptions([]);
        handleFilterChange([]);
      } else {
        setSelectedOptions([singleOptionValue]);
        handleFilterChange([singleOptionValue]);
      }
      return;
    }

    // Handle normal multi-option cases
    if (!excludeGaleMarketSelWithOtherBranch) {
      if (
        selectedOptions?.length === options?.length ||
        (arr1?.length === arr2?.length && arr1?.length > 1)
      ) {
        setSelectedOptions([]);
        handleFilterChange([]);
      } else {
        let values = options.map((option) => option?.value);
        setSelectedOptions(values);
        handleFilterChange(values);
      }
    } else {
      if (
        selectedOptions.length === options?.length ||
        selectedOptions?.length === (!regionalBranches ? options?.length - 1 : options?.length)
      ) {
        setSelectedOptions([]);
        handleFilterChange([]);
      } else {
        let values = options.map((option) => option?.value).filter((value) => value !== 1);
        setSelectedOptions(values);
        handleFilterChange(values);
      }
    }
  };

  useEffect(() => {
    if (resetValues) {
      setSingleSelectValue(singleSelectDefaultValue);
      setSelectedOptions([]);
      handleResetValues();
    }
  }, [resetValues, singleSelectDefaultValue]);

  useEffect(() => {
    if (updateSingleDropdownValue?.length) {
      setSingleSelectValue(updateSingleDropdownValue);
      setSelectedOptions(updateSingleDropdownValue);
    }
  }, [updateSingleDropdownValue]);

  useEffect(() => {
    if (updateMultipleDropdownValue?.length) {
      setMultiSelectValues(updateMultipleDropdownValue);
      setSelectedOptions(updateMultipleDropdownValue);
    }
  }, [updateMultipleDropdownValue]);

  useEffect(() => {
    // to clear the selected options for multiselect in overall based on the region selection change
    if (excludeGaleMarketSelWithOtherBranch && regionIdDependencyValue) {
      setSelectedOptions([]);
    }
  }, [regionIdDependencyValue]);

  useEffect(() => {
    if (selectedOptions?.length !== value?.length && value?.length > 0) {
      setSelectedOptions(value);
    }
  }, [value]);

  // Added function to replace nested ternary
  const getSelectAllText = () => {
    // If there are no options, default to "Select All"
    if (!options || options.length === 0) {
      return 'Select All';
    }

    // Case 1: When there's only one option available
    if (options.length === 1) {
      return selectedOptions.includes(options[0].value) ? 'Deselect All' : 'Select All';
    }

    // Case 2: When excludeGaleMarketSelWithOtherBranch is true and all options are selected
    if (excludeGaleMarketSelWithOtherBranch && selectedOptions.length === options.length) {
      return 'Deselect All';
    }

    // Case 3: When all options are selected (by comparing arrays)
    if (arr1?.length === arr2?.length && arr1?.length > 1) {
      return 'Deselect All';
    }

    // Case 4: When all options (or all but Gale Markets) are selected
    const expectedLength =
      excludeGaleMarketSelWithOtherBranch && !regionalBranches
        ? options.length - 1
        : options.length;
    if (selectedOptions.length === expectedLength) {
      return 'Deselect All';
    }

    // Default case
    return 'Select All';
  };

  return (
    <div style={containerWidth?.length ? { width: containerWidth } : {}}>
      {multiple ? (
        isSelectAll ? (
          <FormControl
            variant="standard"
            sx={
              className.includes('promotion-filter')
                ? { m: marginExist ? 1 : 0, mb: 0, minWidth: 120 }
                : { m: marginExist ? 1 : 0, minWidth: 120, width }
            }
          >
            {label && <InputLabel>{label}</InputLabel>}
            <Select
              multiple
              value={selectedOptions}
              onChange={handleSelectedOption}
              label={label}
              disabled={disabled}
              sx={{ width }}
              renderValue={() => {
                return arr1?.length === arr2?.length && arr1?.length > 1
                  ? allOptionsSelectedText
                  : arr1.join(', ');
              }}
            >
              <>
                <MenuItem key="select-all" onClick={handleSelectAll}>
                  <span style={{ paddingLeft: '21px' }}>{getSelectAllText()}</span>
                </MenuItem>
                {options?.map((opt) => (
                  <MenuItem
                    key={`option-${opt.value}`}
                    value={opt.value}
                    onClick={() => handleSelectedOption(opt?.value)}
                    style={{
                      backgroundColor: selectedOptions?.includes(opt.value)
                        ? 'rgba(255, 222, 0, 0.08)'
                        : '',
                    }}
                  >
                    {selectedOptions.includes(opt.value) ? (
                      <CheckCircleOutlineIcon
                        sx={{ color: '#064e06f2', marginRight: '5px' }}
                        fontSize="small"
                      />
                    ) : (
                      <div style={{ marginRight: '25px' }}></div>
                    )}
                    {opt.label}
                  </MenuItem>
                ))}
              </>
            </Select>
          </FormControl>
        ) : (
          <FormControl
            variant="standard"
            sx={{ m: marginExist ? 1 : 0, minWidth: 120, width: width }}
          >
            {label && <InputLabel>{label}</InputLabel>}
            <Select
              multiple
              value={multiSelectValues}
              onChange={handleMultiSelectChange}
              label={label}
              sx={{ width }}
              disabled={disabled}
              renderValue={() => {
                let arr1 = options
                  ?.filter((opt) => multiSelectValues.includes(opt.value))
                  .map((opt) => opt.label);
                let arr2 = options?.filter((opt) => opt.label);
                return arr1?.length === arr2?.length && arr1?.length > 1
                  ? allOptionsSelectedText
                  : arr1.join(', ');
              }}
            >
              {options?.map((opt) => (
                <MenuItem key={`multi-option-${opt.value}`} value={opt.value}>
                  {multiSelectValues.includes(opt.value) ? (
                    <CheckCircleOutlineIcon
                      sx={{ color: '#064e06f2', marginRight: '5px' }}
                      fontSize="small"
                    />
                  ) : (
                    <div style={{ marginRight: '25px' }}></div>
                  )}
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      ) : (
        <FormControl
          variant="standard"
          sx={{ m: marginExist ? 1 : 0, minWidth: 120, width: width }}
        >
          {label && <InputLabel>{label}</InputLabel>}
          <Select
            value={singleSelectValue}
            onChange={handleSingleSelectChange}
            label={label}
            sx={{ width }}
            disabled={disabled}
            renderValue={() => {
              let selectedItem = options?.find((opt) => singleSelectValue === opt?.value);
              return selectedItem?.dropdown_value_label === ''
                ? selectedItem?.dropdown_value_label
                : selectedItem?.label || null;
            }}
          >
            {options
              .filter((opt) => opt !== undefined)
              ?.map((opt) => (
                <MenuItem
                  key={`single-option-${opt.value}`}
                  value={opt.value}
                  disabled={opt?.id === 'none-headline'}
                >
                  {singleSelectValue === opt.value ? (
                    <CheckCircleOutlineIcon
                      sx={{ color: '#064e06f2', marginRight: '5px' }}
                      fontSize="small"
                    />
                  ) : (
                    <div style={{ marginRight: '25px' }}></div>
                  )}
                  {opt.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      )}
    </div>
  );
};

SelectDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  width: PropTypes.string,
  label: PropTypes.string,
  singleSelectDefaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  multiSelectDefaultValues: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  className: PropTypes.string,
  handleFilterChange: PropTypes.func,
  resetValues: PropTypes.bool,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  allOptionsSelectedText: PropTypes.string,
  updateSingleDropdownValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  updateMultipleDropdownValue: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  isSelectAll: PropTypes.bool,
  containerWidth: PropTypes.string,
  handleResetValues: PropTypes.func,
  marginExist: PropTypes.bool,
  excludeGaleMarketSelWithOtherBranch: PropTypes.bool,
  regionalBranches: PropTypes.bool,
  regionIdDependencyValue: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
};

export default SelectDropdown;
